import { computed, ref, watch } from 'vue'

import { acceptHMRUpdate, defineStore } from 'pinia'
import { useAuthStore } from './auth';

import { getClientById } from '@/Stores/Api/client';
import { getCaregiverById } from '@/Stores/Api/caregiver';

import moment from 'moment'
import { deepCopy, isAddressComplete } from '@/Helpers';
import { set } from 'nprogress';

/*
    This store is for the Gifting & Engagement page and its Opportunities and History modules.
*/

export const useEngagementStore = defineStore('engagement', () => {

    const authStore = useAuthStore()

    const opportunitiesData = ref([])
    const scheduledEngagementData = ref([])

    const showAllScheduledRequests = ref(false)

    const lastUpdated = ref(null)

    const loading = ref(false)

    /**
     * @property {number|null} id
     * @property {string} message
     * @property {Object} recipient
     * @property {number} recipient.id
     * @property {string} recipient.first_name
     * @property {string} recipient.last_name
     * @property {string} recipient.type
     * @property {Array} recipient.latest_rating_per_job
     * @property {Object|null} parent
     * @property {number} parent.id
     * @property {string} parent.first_name
     * @property {string} parent.last_name
     * @property {string} parent.type
     * @property {Array} parent.latest_rating_per_job
     * @property {Object|null} occasion
     * @property {number} occasion.id
     * @property {string} occasion.name
     * @property {Object|null} gift
     * @property {number} gift.id
     * @property {string} gift.name
     * @property {Array} from
     * @property {Array} from[].id
     * @property {string} from[].first_name
     * @property {string} from[].last_name
     * @property {Object} address
     * @property {string} address.address
     * @property {string} address.address_2
     * @property {string} address.city
     * @property {string} address.state
     * @property {string} address.zip
     * @property {boolean} address_validated
     */
    const selectedGiftRequest = ref(null)
    const selectedGiftRequestLoading = ref(false)

    function opportunityToGiftRequest(opportunity, contact = null) {
        let parentType = ''
        if (opportunity.is_client) {
            parentType = 'client'
        } else if (opportunity.is_caregiver) {
            parentType = 'caregiver'
        } else {
            parentType = 'coi'
        }
        let child = null
        let address = null

        const parent = {
            id: opportunity.id,
            type: parentType,
            first_name: opportunity.first_name,
            last_name: opportunity.last_name,
            latest_rating_per_job: opportunity.latest_rating_per_job,
            staff: opportunity.assigned_staff,
        }
        if (contact) {
            address = {
                address: contact.address,
                address_2: contact.address_2,
                city: contact.city,
                state: contact.state,
                zip: contact.zip
            }
            child = {
                id: contact.id,
                type: 'contact',
                first_name: contact.first_name,
                last_name: contact.last_name,
            }
        } else {
            address = {...opportunity.address}
        }

        const data = {
            id: null,
            message: '',
            address: {...address},
            from: [],
            propagate_address: !isAddressComplete(
                address.address,
                address.address_2,
                address.city,
                address.state,
                address.zip
            ),
            gift: null,
            occasion: null
        }

        if (child) {
            data.recipient = {...child, parent: {...parent} }
        } else {
            data.recipient = {...parent}
        }

        return data
    }

    function quickSearchResultToGiftRequest(quickSearchResult) {
        const data = {
            id: null,
            message: '',
            address: {...quickSearchResult.address},
            from: [],
            propagate_address: !isAddressComplete(
                quickSearchResult.address.address,
                quickSearchResult.address.address_2,
                quickSearchResult.address.city,
                quickSearchResult.address.state,
                quickSearchResult.address.zip
            ),
            gift: null,
            occasion: null,
            recipient: {
                id: quickSearchResult.id,
                type: quickSearchResult.type[0],
                first_name: quickSearchResult.first_name,
                last_name: quickSearchResult.last_name,
            }
        }

        return data
    }

    function caregiverToGiftRequest(caregiverData) {
        return {
            id: null,
            message: '',
            address: {...caregiverData.address},
            from: [],
            propagate_address: !isAddressComplete(
                caregiverData.address.address,
                caregiverData.address.address_2,
                caregiverData.address.city,
                caregiverData.address.state,
                caregiverData.address.zip
            ),
            gift: null,
            occasion: null,
            recipient: {
                id: caregiverData.id,
                type: 'caregiver',
                first_name: caregiverData.first_name,
                last_name: caregiverData.last_name,
                latest_rating_per_job: caregiverData.latest_rating_per_job,
                staff: caregiverData.assigned_recruiter,
            }
        }
    }

    function clientToGiftRequest(clientData) {
        return {
            id: null,
            message: '',
            address: {...clientData.address},
            from: [],
            propagate_address: !isAddressComplete(
                clientData.address.address,
                clientData.address.address_2,
                clientData.address.city,
                clientData.address.state,
                clientData.address.zip
            ),
            gift: null,
            occasion: null,
            recipient: {
                id: clientData.id,
                type: 'client',
                first_name: clientData.first_name,
                last_name: clientData.last_name,
                latest_rating_per_job: clientData.latest_rating_per_job,
                staff: clientData.assigned_sales_manager,
            }
        }
    }

    async function selectRequest(request_id) {
        selectedGiftRequestLoading.value = true
        const requestFromLocalStore = scheduledEngagementData.value.find(request => request.id === request_id)
        console.log('Selecting request ID:', request_id, 'Found in local store:', requestFromLocalStore)
        if (!requestFromLocalStore) {
            selectedGiftRequestLoading.value = false
            return
        }

        selectedGiftRequest.value = deepCopy(requestFromLocalStore)
        selectedGiftRequest.value.recipient.staff = requestFromLocalStore.recipient.assigned_staff
        delete selectedGiftRequest.value.recipient.assigned_staff
        selectedGiftRequestLoading.value = false
    }

    async function selectOpportunity(recipient_id, recipient_type) {
        selectedGiftRequestLoading.value = true;
        const resourceFromLocalStore = scheduledEngagementData.value.find(request => request.recipient.id === recipient_id && request.recipient.type === recipient_type)
        if (resourceFromLocalStore) {
            selectedGiftRequest.value = deepCopy(resourceFromLocalStore)
            selectedGiftRequestLoading.value = false
            return
        }

        try {
            const response = await getEngagementGiftRequests({ recipient_id, recipient_type })
            if (response.data.length > 0) {
                addEngagementGiftRequestToLocalStore(response.data[0])
                selectRequest(response.data[0].id)
                return
            }
        } catch (error) {
            console.error('Error fetching engagement gift requests:', error)
        }

        let contact = null
        let parentOpportunity = null
        if (recipient_type === 'contact') {
            parentOpportunity = opportunitiesData.value.find(opp => opp.contacts && opp.contacts.some(contact => contact.id === recipient_id))
            contact = parentOpportunity.contacts.find(contact => contact.id === recipient_id)
        } else {
            parentOpportunity = opportunitiesData.value.find(opp => opp.id === recipient_id && opp[`is_${recipient_type}`] === true)
        }
        if (!parentOpportunity) {
            selectedGiftRequestLoading.value = false
            return
        }

        selectedGiftRequest.value = opportunityToGiftRequest(parentOpportunity, contact)
        selectedGiftRequestLoading.value = false
    }

    const map = {
        'prospect': 'client',
        'client': 'client',
        'applicant': 'caregiver',
        'candidate': 'caregiver',
        'coi': 'coi'
    }
    async function selectQuickSearchResult(quickSearchResult) {
        const recipient_id = quickSearchResult.id
        const recipient_type = map[quickSearchResult.type[0]] || null

        selectedGiftRequestLoading.value = true;
        const requestFromLocalStore = scheduledEngagementData.value.find(request => request.id === recipient_id && request.recipient.type === recipient_type)
        if (requestFromLocalStore) {
            selectedGiftRequest.value = deepCopy(requestFromLocalStore)
            selectedGiftRequestLoading.value = false
            return
        }

        if (recipient_type === 'coi') {
            selectedGiftRequest.value = quickSearchResultToGiftRequest(quickSearchResult);
            selectedGiftRequestLoading.value = false
            return
        }

        if (!['client', 'caregiver'].includes(recipient_type)) {
            selectedGiftRequestLoading.value = false
            return
        }

        Promise.all([
            getEngagementGiftRequests({ recipient_id: recipient_id, recipient_type: recipient_type }),
            recipient_type === 'client' ? getClientById(recipient_id, ['latest_rating_per_job']) : getCaregiverById(recipient_id, ['latest_rating_per_job'])
        ]).then(([giftRequestsResponse, resourceResponse]) => {
            console.log('Gift Requests Response:', giftRequestsResponse)
            console.log('Resource Response:', resourceResponse)
            if (giftRequestsResponse.data.length > 0) {
                addEngagementGiftRequestToLocalStore(giftRequestsResponse.data[0])
                selectRequest(giftRequestsResponse.data[0].id)
                return
            }

            let giftRequestData = null
            if (recipient_type === 'client') {
                giftRequestData = clientToGiftRequest(resourceResponse.data)
            } else if (recipient_type === 'caregiver') {
                giftRequestData = caregiverToGiftRequest(resourceResponse.data)
            }

            selectedGiftRequest.value = giftRequestData

        }).catch((err) => {
            console.error('Error fetching data for quick search result:', err)
        }).finally(() => {
            selectedGiftRequestLoading.value = false
        })

    }

    function hydrateOpportunities(data) {
        if (!Array.isArray(data)) {
            console.warn('Expected array for opportunities data');
            return;
        }

        opportunitiesData.value = data

        lastUpdated.value = moment.utc()

    }

    function hydrateGiftRequests(data) {

        if (!Array.isArray(data)) {
            console.warn('Expected array for gift requests data');
            return;
        }

        scheduledEngagementData.value = data
    }

    const giftOptions = ref([
        {
            id: 1,
            label: 'Note Card',
            description: 'A simple note card to include a personal message.',
            icon: 'note-card'
        },
        {
            id: 2,
            label: 'Small Gift',
            description: 'A small gift recipient to show appreciation.',
            icon: 'gift-small'
        },
        {
            id: 3,
            label: 'Large Gift',
            description: 'A larger gift recipient for special occasions.',
            icon: 'gift-large'
        },
    ])

    function addEngagementGiftRequestToLocalStore(data) {
        scheduledEngagementData.value.unshift(data);
    }

    function updateEngagementGiftRequestInLocalStore(id, data) {
        const index = scheduledEngagementData.value.findIndex(request => request.id === id);
        if (index !== -1) {
            scheduledEngagementData.value[index] = { ...data };
        }
    }

    function removeEngagementGiftRequestFromLocalStore(id) {
        scheduledEngagementData.value = scheduledEngagementData.value.filter(request => request.id !== id);
    }

    //
    // API Requests
    //

    async function getEngagementOpportunities(staff_user_id) {
        return await new Promise((resolve, reject) => {
            loading.value = true
            axios.get(
                `/api/v1/staffs/${staff_user_id}/engagement-recipients`
            ).then(response => {
                resolve(response)
            }).catch((err) => {
                authStore.handleError(err)
                reject({ "error": err })
            }).finally(() => {
                loading.value = false
            })
        });
    }

    async function getEngagementGiftRequests(additionalParams = {}, triggerLoading = true) {
        const params = {
            occasion_id: 1,
            ...additionalParams
        }
        return await new Promise((resolve, reject) => {
            if (triggerLoading) {
                loading.value = true
            }
            axios.get(
                `/api/v1/engagement-gift-requests`,
                {
                    params: params
                }
            ).then(response => {
                resolve(response)
            }).catch((err) => {
                authStore.handleError(err)
                reject({ "error": err })
            }).finally(() => {
                if (triggerLoading) {
                    loading.value = false
                }
            })
        });
    }

    async function storeEngagementGiftRequest(data) {
        return await new Promise((resolve, reject) => {
            loading.value = true
            axios.post(
                `/api/v1/engagement-gift-requests`,
                data
            ).then(function (response) {
                if (response.status === 201) {
                    const redirectUrl = response.headers.location;
                    return axios.get(redirectUrl);
                }
            }).then(response => {
                resolve(response)
            }).catch((err) => {
                authStore.handleError(err)
                reject({ "error": err })
            }).finally(() => {
                loading.value = false
            })
        });
    }

    async function updateEngagementGiftRequest(id, data) {
        return await new Promise((resolve, reject) => {
            loading.value = true
            axios.patch(
                `/api/v1/engagement-gift-requests/${id}`,
                data
            ).then(response => {
                resolve(response)
            }).catch((err) => {
                authStore.handleError(err)
                reject({ "error": err })
            }).finally(() => {
                loading.value = false
            })
        });
    }

    async function deleteEngagementGiftRequest(id) {
        return await new Promise((resolve, reject) => {
            loading.value = true
            axios.delete(
                `/api/v1/engagement-gift-requests/${id}`
            ).then(response => {
                resolve(response)
            }).catch((err) => {
                authStore.handleError(err)
                reject({ "error": err })
            }).finally(() => {
                loading.value = false
            })
        });
    }

    //Polling logic

    const pollingTimeout = ref(null)
    const waitBeforePolling = 10000

    function setupPolling() {
        if (pollingTimeout.value) {
            return
        }
        pollingTimeout.value = setTimeout(() => {
            getEngagementGiftRequests({}, false).then(response => {
                hydrateGiftRequests(response.data)
            }).catch((err) => {
                console.error('Error during polling for engagement gift requests:', err)
            }).finally(() => {
                if (!selectedGiftRequest.value || !selectedGiftRequest.value.id) {
                    pollingTimeout.value = null
                    setupPolling()
                }
            })
        }, waitBeforePolling)
    }
    watch(() => selectedGiftRequest.value, (newVal) => {
        if (newVal && newVal.id) {
            clearTimeout(pollingTimeout.value)
            pollingTimeout.value = null
            return
        }
        setupPolling()
    }, { immediate: true })

    return {
        getEngagementOpportunities, hydrateOpportunities,
        getEngagementGiftRequests, hydrateGiftRequests,
        storeEngagementGiftRequest, addEngagementGiftRequestToLocalStore,
        updateEngagementGiftRequest, updateEngagementGiftRequestInLocalStore,
        deleteEngagementGiftRequest, removeEngagementGiftRequestFromLocalStore,
        giftOptions,
        selectRequest, selectOpportunity, selectQuickSearchResult,
        selectedGiftRequest, selectedGiftRequestLoading,
        opportunitiesData, scheduledEngagementData, showAllScheduledRequests,
        lastUpdated, loading
    }

})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useEngagementStore, import.meta.hot))
}
